/**
 * Phase 5F.2 — Public tokenized payment link.
 * No auth required. Friendly states for active / expired / invalid / revoked.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CreditCard, AlertTriangle, ShieldCheck, ArrowRight, Lock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL });

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center p-4">
    <div className="max-w-md w-full">
      <div className="text-center mb-6">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-red-700">Winhouse Tutors</div>
        <div className="text-xs text-gray-500 mt-1">Secure Payment</div>
      </div>
      {children}
      <div className="mt-6 flex items-center justify-center gap-1.5 text-[10px] text-gray-400">
        <Lock className="h-3 w-3" />
        <span>Encrypted checkout · Pricing in AUD · Converted to USD at checkout</span>
      </div>
    </div>
  </div>
);

const ErrorCard = ({ icon: Icon, title, desc, testid }) => (
  <div className="bg-white rounded-2xl border-2 border-rose-200 p-7 text-center" data-testid={testid}>
    <div className="w-14 h-14 mx-auto rounded-full bg-rose-50 flex items-center justify-center mb-3">
      <Icon className="h-7 w-7 text-rose-700" />
    </div>
    <h2 className="text-lg font-bold text-rose-900">{title}</h2>
    <p className="text-sm text-gray-600 mt-2">{desc}</p>
    <p className="text-[11px] text-gray-500 mt-4">
      Need help? Email{' '}
      <a href="mailto:info@winhousetutors.com" className="text-red-700 underline">info@winhousetutors.com</a>
    </p>
  </div>
);

const PublicPaymentLinkPage = () => {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const r = await ax.get(`/api/public/payment-link/${token}`);
        if (!live) return;
        setInfo(r.data);
        setAmount(r.data?.amount_aud != null ? r.data.amount_aud.toFixed(2) : '');
        if (r.data?.status && r.data.status !== 'active') setError(r.data.status);
      } catch (e) {
        if (live) setError(e.response?.status === 404 ? 'invalid' : 'network');
      } finally { if (live) setLoading(false); }
    })();
    return () => { live = false; };
  }, [token]);

  const pay = async () => {
    setBusy(true);
    try {
      const body = info?.allow_payer_edit_amount ? { amount_aud: parseFloat(amount) } : {};
      const r = await ax.post(`/api/public/payment-link/${token}/checkout`, body);
      const url = r.data?.redirect_url;
      if (url) window.location.href = url;
      else setError('network');
    } catch (e) {
      if (e.response?.status === 410) setError('expired');
      else if (e.response?.status === 404) setError('invalid');
      else setError(e.response?.data?.detail || 'network');
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <Shell>
        <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center" data-testid="ppl-loading">
          <Loader2 className="h-7 w-7 animate-spin text-red-700 mx-auto" />
          <p className="text-xs text-gray-500 mt-3">Loading secure checkout…</p>
        </div>
      </Shell>
    );
  }

  if (error === 'expired') {
    return (
      <Shell>
        <ErrorCard icon={AlertTriangle} title="This payment link has expired"
          desc="The link you used is past its expiry date. Please contact your tutor or admin for a fresh link." testid="ppl-expired" />
      </Shell>
    );
  }
  if (error === 'invalid' || error === 'revoked') {
    return (
      <Shell>
        <ErrorCard icon={AlertTriangle} title="Payment link not found"
          desc="This link is invalid or has been revoked. Please request a new payment link from your tutor or Winhouse admin." testid="ppl-invalid" />
      </Shell>
    );
  }
  if (error) {
    return (
      <Shell>
        <ErrorCard icon={AlertTriangle} title="Something went wrong"
          desc={typeof error === 'string' ? error : 'Please try again in a moment.'} testid="ppl-error" />
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="bg-white rounded-2xl border border-gray-200 p-7" data-testid="ppl-active">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
            <ShieldCheck className="h-5 w-5 text-emerald-700" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Verified payment link</div>
            <div className="text-xs text-gray-500">{info?.plan_name || info?.description || 'Winhouse Learning Plan'}</div>
          </div>
        </div>

        {info?.student_name && (
          <div className="text-sm text-gray-700 mb-2">
            For <strong className="text-gray-900" data-testid="ppl-student">{info.student_name}</strong>
            {info?.program ? <span className="text-gray-500"> · {info.program}</span> : null}
          </div>
        )}

        {info?.subjects_preview?.length > 0 && (
          <div className="text-[11px] text-gray-500 mb-3" data-testid="ppl-subjects">
            Subjects: {info.subjects_preview.join(' · ')}
          </div>
        )}

        {info?.description && (
          <p className="text-[12px] text-gray-600 italic mb-3" data-testid="ppl-description">&ldquo;{info.description}&rdquo;</p>
        )}

        {/* Amount block */}
        {info?.allow_payer_edit_amount ? (
          <label className="block mb-3" data-testid="ppl-amount-editable">
            <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Amount (AUD)</span>
            <input type="number" min="1" step="0.01" value={amount} onChange={e => setAmount(e.target.value)}
              className="mt-1 w-full border-2 border-gray-200 focus:border-red-700 outline-none rounded-xl px-3 py-2.5 text-xl font-bold"
              data-testid="ppl-amount-input" />
            <span className="text-[10px] text-gray-500 mt-1 inline-block">You can adjust this amount before paying.</span>
          </label>
        ) : (
          <div className="bg-[#FAF9F6] rounded-xl p-4 mb-3" data-testid="ppl-amount-fixed">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Amount due</div>
            <div className="text-3xl font-bold text-gray-900 mt-1">AUD {(info?.amount_aud ?? 0).toFixed(2)}</div>
          </div>
        )}

        {info?.due_date && (
          <div className="text-[11px] text-amber-700 font-semibold mb-3">Due by {info.due_date}</div>
        )}

        <button onClick={pay} disabled={busy || (info?.allow_payer_edit_amount && (!amount || parseFloat(amount) < 1))}
          className="w-full bg-black hover:bg-gray-900 disabled:opacity-50 text-white py-3.5 rounded-full font-bold uppercase tracking-wider text-xs flex items-center justify-center gap-2 transition"
          data-testid="ppl-pay-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
          {busy ? 'Redirecting…' : 'Pay securely'}
          {!busy && <ArrowRight className="h-4 w-4" />}
        </button>

        <p className="text-[10px] text-gray-400 mt-3 text-center">
          You&apos;ll be redirected to our secure payment partner. AUD will be charged equivalent in USD at the live FX rate.
        </p>
      </div>
    </Shell>
  );
};

export default PublicPaymentLinkPage;
